import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../App';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  ArrowLeft, Save, PenTool, CheckSquare, Circle,
  Type, Calendar, Trash2, Plus, MousePointer, Eye,
  ChevronLeft, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import DocumentPreview from '../components/DocumentPreview';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `${window.location.origin}/pdf.worker.min.mjs`;

const FIELD_TYPES = {
  SIGNATURE: 'signature',
  INITIALS: 'initials',
  DATE: 'date',
  TEXT: 'text',
  EMAIL: 'email',
  NAME: 'name',
  COMPANY: 'company',
  TITLE: 'title',
  CHECKBOX: 'checkbox',
  RADIO: 'radio',
  DROPDOWN: 'dropdown'
};

const DocumentFieldEditor = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { API, accessToken } = useContext(AuthContext);
  
  // Get data from navigation state
  const { fileId, fileName, documentTitle } = location.state || {};
  
  const [pdfFile, setPdfFile] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [fields, setFields] = useState([]);
  const [selectedFieldType, setSelectedFieldType] = useState(null);
  const [selectedField, setSelectedField] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const containerRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
  const [isMovingField, setIsMovingField] = useState(false);
  const [movingFieldId, setMovingFieldId] = useState(null);
  const [pdfPageDimensions, setPdfPageDimensions] = useState({ width: 612, height: 792 });
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

  // Calculate scale to fit page in container (always fit full page, no scrolling)
  const calculateFitScale = (pageWidth, pageHeight, containerWidth, containerHeight) => {
    if (!pageWidth || !pageHeight || !containerWidth || !containerHeight) {
      return 1.0;
    }
    // Calculate scale to fit both width and height, use the smaller one
    const scaleX = containerWidth / pageWidth;
    const scaleY = containerHeight / pageHeight;
    // Use 95% of the calculated scale to add some padding
    return Math.min(scaleX, scaleY) * 0.95;
  };

  // Dynamic scale based on container and page dimensions
  const scale = calculateFitScale(
    pdfPageDimensions.width,
    pdfPageDimensions.height,
    containerSize.width,
    containerSize.height
  );

  // Update container size on mount and resize
  useEffect(() => {
    const updateContainerSize = () => {
      // Available height = viewport height - header - toolbar - padding
      // Using a fixed offset that accounts for all UI chrome
      const availableHeight = window.innerHeight - 220;
      // Available width = approximate width of the PDF column (col-span-7 of 12)
      const availableWidth = window.innerWidth * 0.55; // ~58% for col-span-7
      setContainerSize({ width: availableWidth, height: availableHeight });
    };

    updateContainerSize();
    window.addEventListener('resize', updateContainerSize);
    return () => window.removeEventListener('resize', updateContainerSize);
  }, []);

  // Handle page load to get original PDF page dimensions
  const onPageLoadSuccess = (page) => {
    // react-pdf provides width/height at scale 1.0
    const width = page.originalWidth || page.width || 612;
    const height = page.originalHeight || page.height || 792;
    setPdfPageDimensions({ width, height });
  };

  // Handle PDF document load success
  const onDocumentLoadSuccess = ({ numPages: pages }) => {
    setNumPages(pages);
    setPageNumber(1);
    console.log(`PDF loaded: ${pages} pages`);
  };

  useEffect(() => {
    if (!fileId) {
      toast.error('No document selected');
      navigate('/send');
      return;
    }

    // Fetch PDF with authentication
    const fetchPDF = async () => {
      try {
        const response = await axios.get(`${API}/files/${fileId}`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          responseType: 'arraybuffer'
        });

        const arrayBuffer = response.data;

        // Create blob for react-pdf
        const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
        setPdfFile(blob);
        
        toast.success('Document loaded successfully!');
      } catch (error) {
        console.error('Failed to load PDF:', error);
        toast.error('Failed to load document');
      }
    };
    
    fetchPDF();

    // No cleanup needed - pdfFile is a Blob, not a URL
  }, [fileId, accessToken, API]);

  // Removed - using iframe instead of react-pdf

  const handleAddField = (e) => {
    if (!selectedFieldType && !isMovingField) return;

    const rect = containerRef.current.getBoundingClientRect();

    const x = (e.clientX - rect.left) / scale;
    const y = (e.clientY - rect.top) / scale;
    
    if (isMovingField && movingFieldId) {
      // Finalize moving an existing field
      setFields(fields.map(f => 
        f.id === movingFieldId 
          ? { ...f, x: Math.max(0, x - 100), y: Math.max(0, y - 30) } // Center the field on cursor
          : f
      ));
      setIsMovingField(false);
      setMovingFieldId(null);
      setSelectedField(null);
      toast.success('Field placed');
      return;
    }
    
    // Determine field dimensions
    let width, height;
    if (selectedFieldType === FIELD_TYPES.SIGNATURE) {
      width = 200; height = 60;
    } else if (selectedFieldType === FIELD_TYPES.INITIALS) {
      width = 80; height = 60;
    } else if (selectedFieldType === FIELD_TYPES.CHECKBOX) {
      width = 20; height = 20;
    } else if (selectedFieldType === FIELD_TYPES.RADIO) {
      // Radio group needs more space to show options
      width = 120; height = 60;
    } else if (selectedFieldType === FIELD_TYPES.DATE) {
      width = 150; height = 30;
    } else {
      width = 150; height = 30;
    }
    
    // Place new field centered on cursor
    const newField = {
      id: `field_${Date.now()}`,
      type: selectedFieldType,
      page: pageNumber,
      x: Math.max(0, x - width / 2),
      y: Math.max(0, y - height / 2),
      width,
      height,
      label: `${selectedFieldType} ${fields.length + 1}`,
      required: true,
      recipientIndex: 0,
      options: (selectedFieldType === FIELD_TYPES.RADIO || selectedFieldType === FIELD_TYPES.DROPDOWN) 
        ? ['Option 1', 'Option 2'] 
        : []
    };
    
    setFields([...fields, newField]);
    setSelectedFieldType(null);
    toast.success('Field placed');
  };

  const handleFieldDoubleClick = (e, field) => {
    e.stopPropagation();
    setIsMovingField(true);
    setMovingFieldId(field.id);
    setSelectedField(field);
    toast.info('Move the field to new position and click to place');
  };

  const handleFieldMouseDown = (e, field) => {
    e.stopPropagation();
    setSelectedField(field);
  };

  const handleMouseMove = (e) => {
    // Always track cursor position for immediate ghost field display
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();

      setCursorPosition({
        x: (e.clientX - rect.left) / scale,
        y: (e.clientY - rect.top) / scale
      });
    }

    // Update moving field position to follow cursor
    if (isMovingField && movingFieldId) {
      const rect = containerRef.current.getBoundingClientRect();

      const x = (e.clientX - rect.left) / scale;
      const y = (e.clientY - rect.top) / scale;

      setFields(fields.map(f =>
        f.id === movingFieldId
          ? { ...f, x: Math.max(0, x - f.width / 2), y: Math.max(0, y - f.height / 2) }
          : f
      ));
    }
  };

  const handleDeleteField = (fieldId) => {
    setFields(fields.filter(f => f.id !== fieldId));
    setSelectedField(null);
    toast.success('Field deleted');
  };

  const handleUpdateField = (fieldId, updates) => {
    setFields(fields.map(f => f.id === fieldId ? { ...f, ...updates } : f));
  };

  const handleContinue = () => {
    // Store fields in sessionStorage and navigate back
    sessionStorage.setItem('documentFields', JSON.stringify(fields));
    toast.success(`${fields.length} fields configured`);
    navigate('/send', { state: { fields, fileId, documentTitle } });
  };

  const getFieldIcon = (type) => {
    switch (type) {
      case FIELD_TYPES.SIGNATURE: return <PenTool size={14} />;
      case FIELD_TYPES.INITIALS: return <span className="text-xs font-bold">IN</span>;
      case FIELD_TYPES.DATE: return <Calendar size={14} />;
      case FIELD_TYPES.TEXT: return <Type size={14} />;
      case FIELD_TYPES.EMAIL: return <span className="text-xs font-bold">@</span>;
      case FIELD_TYPES.NAME: return <span className="text-xs font-bold">N</span>;
      case FIELD_TYPES.COMPANY: return <span className="text-xs font-bold">C</span>;
      case FIELD_TYPES.TITLE: return <span className="text-xs font-bold">T</span>;
      case FIELD_TYPES.CHECKBOX: return <CheckSquare size={14} />;
      case FIELD_TYPES.RADIO: return <Circle size={14} />;
      case FIELD_TYPES.DROPDOWN: return <span className="text-xs font-bold">▼</span>;
      default: return null;
    }
  };

  const getFieldColor = (type) => {
    switch (type) {
      case FIELD_TYPES.SIGNATURE: return 'bg-purple-500';
      case FIELD_TYPES.INITIALS: return 'bg-purple-400';
      case FIELD_TYPES.DATE: return 'bg-pink-500';
      case FIELD_TYPES.TEXT: return 'bg-yellow-500';
      case FIELD_TYPES.EMAIL: return 'bg-orange-500';
      case FIELD_TYPES.NAME: return 'bg-blue-400';
      case FIELD_TYPES.COMPANY: return 'bg-cyan-500';
      case FIELD_TYPES.TITLE: return 'bg-teal-500';
      case FIELD_TYPES.CHECKBOX: return 'bg-green-500';
      case FIELD_TYPES.RADIO: return 'bg-blue-500';
      case FIELD_TYPES.DROPDOWN: return 'bg-indigo-500';
      default: return 'bg-gray-500';
    }
  };

  return (
    <Layout>
      <div
        className="flex flex-col overflow-y-auto"
        style={{ height: 'calc(100vh - 64px)', maxHeight: 'calc(100vh - 64px)' }}
      >
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between flex-shrink-0 mb-2 gap-2" style={{ minHeight: '60px' }}>
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/send')}
            >
              <ArrowLeft size={18} className="mr-2" />
              Back
            </Button>
            <div className="min-w-0">
              <h1 className="text-xl sm:text-3xl font-bold text-gray-900 truncate">Place Signature Fields</h1>
              <p className="text-gray-600 text-sm truncate">{documentTitle || fileName}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
            <Button
              variant="outline"
              onClick={() => setShowPreview(true)}
              disabled={fields.length === 0}
              size="sm"
            >
              <Eye size={16} className="mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Preview Fields</span>
              <span className="sm:hidden">Preview</span>
            </Button>
            <Button
              onClick={handleContinue}
              className="bg-gradient-to-r from-green-600 to-emerald-600"
              size="sm"
            >
              <Save size={16} className="mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Continue to Recipients ({fields.length} fields)</span>
              <span className="sm:hidden">Continue ({fields.length})</span>
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 flex-1" style={{ minHeight: 0 }}>
          {/* Toolbar */}
          <div className="col-span-1 md:col-span-2">
            <Card>
              <CardContent className="pt-6 space-y-2">
                <h3 className="font-semibold mb-4">Field Types</h3>
                
                <div className="text-xs font-semibold text-gray-500 mb-2">SIGNATURE</div>
                <Button
                  variant={selectedFieldType === FIELD_TYPES.SIGNATURE ? 'default' : 'outline'}
                  className="w-full justify-start text-sm"
                  size="sm"
                  onClick={() => setSelectedFieldType(FIELD_TYPES.SIGNATURE)}
                >
                  <PenTool size={14} className="mr-2" />
                  Signature
                </Button>
                
                <Button
                  variant={selectedFieldType === FIELD_TYPES.INITIALS ? 'default' : 'outline'}
                  className="w-full justify-start text-sm"
                  size="sm"
                  onClick={() => setSelectedFieldType(FIELD_TYPES.INITIALS)}
                >
                  <span className="text-xs font-bold mr-2">IN</span>
                  Initials
                </Button>
                
                <Button
                  variant={selectedFieldType === FIELD_TYPES.DATE ? 'default' : 'outline'}
                  className="w-full justify-start text-sm"
                  size="sm"
                  onClick={() => setSelectedFieldType(FIELD_TYPES.DATE)}
                >
                  <Calendar size={14} className="mr-2" />
                  Date
                </Button>

                <div className="text-xs font-semibold text-gray-500 mt-4 mb-2">INPUT FIELDS</div>
                <Button
                  variant={selectedFieldType === FIELD_TYPES.NAME ? 'default' : 'outline'}
                  className="w-full justify-start text-sm"
                  size="sm"
                  onClick={() => setSelectedFieldType(FIELD_TYPES.NAME)}
                >
                  <span className="text-xs font-bold mr-2">N</span>
                  Name
                </Button>
                
                <Button
                  variant={selectedFieldType === FIELD_TYPES.EMAIL ? 'default' : 'outline'}
                  className="w-full justify-start text-sm"
                  size="sm"
                  onClick={() => setSelectedFieldType(FIELD_TYPES.EMAIL)}
                >
                  <span className="text-xs font-bold mr-2">@</span>
                  Email
                </Button>
                
                <Button
                  variant={selectedFieldType === FIELD_TYPES.TEXT ? 'default' : 'outline'}
                  className="w-full justify-start text-sm"
                  size="sm"
                  onClick={() => setSelectedFieldType(FIELD_TYPES.TEXT)}
                >
                  <Type size={14} className="mr-2" />
                  Text
                </Button>

                <div className="text-xs font-semibold text-gray-500 mt-4 mb-2">CHOICES</div>
                <Button
                  variant={selectedFieldType === FIELD_TYPES.CHECKBOX ? 'default' : 'outline'}
                  className="w-full justify-start text-sm"
                  size="sm"
                  onClick={() => setSelectedFieldType(FIELD_TYPES.CHECKBOX)}
                >
                  <CheckSquare size={14} className="mr-2" />
                  Checkbox
                </Button>
                
                <Button
                  variant={selectedFieldType === FIELD_TYPES.RADIO ? 'default' : 'outline'}
                  className="w-full justify-start text-sm"
                  size="sm"
                  onClick={() => setSelectedFieldType(FIELD_TYPES.RADIO)}
                >
                  <Circle size={14} className="mr-2" />
                  Radio
                </Button>

                {selectedFieldType && (
                  <div className="pt-4 border-t">
                    <p className="text-sm text-gray-600">
                      <MousePointer size={14} className="inline mr-1" />
                      Click on document to place
                    </p>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full mt-2"
                      onClick={() => setSelectedFieldType(null)}
                    >
                      Cancel
                    </Button>
                  </div>
                )}

                <div className="pt-4 border-t">
                  <p className="text-sm font-medium mb-2">Fields: {fields.length}</p>
                  <div className="space-y-1 max-h-60 overflow-y-auto">
                    {fields.map((field) => (
                      <div
                        key={field.id}
                        className={`text-xs p-2 rounded cursor-pointer ${
                          selectedField?.id === field.id ? 'bg-indigo-100 border border-indigo-300' : 'bg-gray-50'
                        }`}
                        onClick={() => setSelectedField(field)}
                      >
                        <div className="flex items-center justify-between">
                          <span className="flex items-center gap-1">
                            {getFieldIcon(field.type)}
                            {field.label}
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteField(field.id);
                            }}
                            className="text-red-600 hover:text-red-800"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* PDF Viewer */}
          <div className="col-span-1 md:col-span-7">
            <Card>
              <CardContent className="pt-2 px-2 pb-2">
                {pdfFile ? (
                  <>
                    <div
                      id="pdf-scroll-container"
                      data-testid="pdf-scroll-container"
                      ref={scrollContainerRef}
                      className={`border border-gray-300 rounded bg-gray-100 flex items-center justify-center ${selectedFieldType ? 'cursor-crosshair' : 'cursor-default'}`}
                      style={{
                        height: 'calc(100vh - 220px)',
                        maxHeight: 'calc(100vh - 220px)',
                        overflow: 'auto',
                        WebkitOverflowScrolling: 'touch'
                      }}
                    >
                    <div
                      ref={containerRef}
                      className="relative"
                      style={{
                        display: 'inline-block'
                      }}
                      onClick={selectedFieldType || isMovingField ? handleAddField : undefined}
                      onMouseMove={handleMouseMove}
                    >
                      <Document
                        file={pdfFile}
                        onLoadSuccess={onDocumentLoadSuccess}
                        onLoadError={(error) => {
                          console.error('PDF load error:', error);
                          toast.error('Failed to load PDF document');
                        }}
                        loading={
                          <div className="flex items-center justify-center h-[600px]">
                            <div className="text-center">
                              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
                              <p className="text-gray-600">Loading document...</p>
                            </div>
                          </div>
                        }
                      >
                        <Page
                          pageNumber={pageNumber}
                          scale={scale}
                          renderTextLayer={false}
                          onLoadSuccess={onPageLoadSuccess}
                        />
                      </Document>

                      {/* Ghost field following cursor when placing new field */}
                      {selectedFieldType && (
                        <div
                          className="absolute pointer-events-none"
                          style={{
                            left: `${(cursorPosition.x - (selectedFieldType === FIELD_TYPES.SIGNATURE ? 100 : selectedFieldType === FIELD_TYPES.INITIALS ? 40 : 75)) * scale}px`,
                            top: `${(cursorPosition.y - (selectedFieldType === FIELD_TYPES.SIGNATURE || selectedFieldType === FIELD_TYPES.INITIALS ? 30 : 15)) * scale}px`,
                            width: `${(selectedFieldType === FIELD_TYPES.SIGNATURE ? 200 : selectedFieldType === FIELD_TYPES.INITIALS ? 80 : selectedFieldType === FIELD_TYPES.CHECKBOX || selectedFieldType === FIELD_TYPES.RADIO ? 20 : 150) * scale}px`,
                            height: `${(selectedFieldType === FIELD_TYPES.SIGNATURE || selectedFieldType === FIELD_TYPES.INITIALS ? 60 : selectedFieldType === FIELD_TYPES.CHECKBOX || selectedFieldType === FIELD_TYPES.RADIO ? 20 : 30) * scale}px`,
                            zIndex: 15,
                          }}
                        >
                          <div className={`w-full h-full border-2 border-dashed ${getFieldColor(selectedFieldType)} bg-opacity-60 flex items-center justify-center text-xs font-semibold text-gray-700`}>
                            {getFieldIcon(selectedFieldType)}
                            <span className="ml-1">{selectedFieldType}</span>
                          </div>
                        </div>
                      )}

                      {/* Render fields for current page */}
                      {fields
                        .filter(f => f.page === pageNumber)
                        .map((field) => (
                          <div
                            key={field.id}
                            className={`absolute border-2 ${
                              selectedField?.id === field.id ? 'border-indigo-600 shadow-lg' : 'border-gray-400'
                            } ${isMovingField && movingFieldId === field.id ? 'opacity-80' : ''} ${getFieldColor(field.type)} bg-opacity-40 cursor-pointer hover:border-indigo-400 transition-opacity`}
                            style={{
                              left: `${field.x * scale}px`,
                              top: `${field.y * scale}px`,
                              width: `${field.width * scale}px`,
                              height: `${field.height * scale}px`,
                              zIndex: 10
                            }}
                            onMouseDown={(e) => handleFieldMouseDown(e, field)}
                            onDoubleClick={(e) => handleFieldDoubleClick(e, field)}
                            title="Double-click to move"
                          >
                            {/* Render radio group with options */}
                            {field.type === FIELD_TYPES.RADIO && field.options?.length > 0 ? (
                              <div className="flex flex-col justify-center h-full p-1 overflow-hidden">
                                {field.options.map((option, idx) => (
                                  <div key={idx} className="flex items-center gap-1 text-xs text-gray-700">
                                    <Circle size={10} className="text-gray-500 flex-shrink-0" />
                                    <span className="truncate">{option}</span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="flex items-center justify-center h-full text-xs font-semibold text-gray-700">
                                {getFieldIcon(field.type)}
                                <span className="ml-1">{field.label}</span>
                              </div>
                            )}
                          </div>
                        ))}
                    </div>
                  </div>

                  {/* Page Controls */}
                  <div className="flex items-center justify-between mt-2 flex-shrink-0 py-2">
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPageNumber(Math.max(1, pageNumber - 1))}
                        disabled={pageNumber <= 1}
                      >
                        Previous
                      </Button>
                      <span className="text-sm py-2 px-4">
                        Page {pageNumber} of {numPages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPageNumber(Math.min(numPages, pageNumber + 1))}
                        disabled={pageNumber >= numPages}
                      >
                        Next
                      </Button>
                    </div>

                    <div className="flex items-center gap-2">
                      <span className="text-sm py-2 px-4 text-gray-600">
                        {Math.round(scale * 100)}% (Auto-fit)
                      </span>
                    </div>
                  </div>
                  </>
                ) : (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
                    <p className="text-gray-500">Loading document...</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Properties Panel */}
          <div className="col-span-1 md:col-span-3">
            <Card>
              <CardContent className="pt-6">
                <h3 className="font-semibold mb-4">Field Properties</h3>
                
                {selectedField ? (
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium mb-1 block">Label</label>
                      <Input
                        value={selectedField.label}
                        onChange={(e) => handleUpdateField(selectedField.id, { label: e.target.value })}
                      />
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium mb-1 block">Type</label>
                      <Input value={selectedField.type} disabled />
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium mb-1 block">Recipient Index</label>
                      <Input
                        type="number"
                        min="0"
                        value={selectedField.recipientIndex}
                        onChange={(e) => handleUpdateField(selectedField.id, { recipientIndex: parseInt(e.target.value) })}
                      />
                      <p className="text-xs text-gray-500 mt-1">0 = first recipient, 1 = second, etc.</p>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={selectedField.required}
                        onChange={(e) => handleUpdateField(selectedField.id, { required: e.target.checked })}
                      />
                      <label className="text-sm">Required Field</label>
                    </div>
                    
                    <Button
                      variant="destructive"
                      className="w-full"
                      onClick={() => handleDeleteField(selectedField.id)}
                    >
                      <Trash2 size={16} className="mr-2" />
                      Delete Field
                    </Button>
                  </div>
                ) : (
                  <div>
                    <p className="text-sm text-gray-500 mb-4">
                      Select a field to view and edit its properties
                    </p>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h4 className="font-semibold text-blue-900 mb-2 text-sm">How to Use:</h4>
                      <ul className="text-xs text-blue-800 space-y-1">
                        <li>1. Click a field type from the toolbar</li>
                        <li>2. Field follows your cursor - move to desired position</li>
                        <li>3. Single click to place/stick the field</li>
                        <li>4. Double-click a placed field to move it</li>
                        <li>5. Single click to finalize new position</li>
                        <li>6. Click "Continue" when done</li>
                      </ul>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <DocumentPreview
          pdfUrl={pdfUrl}
          fields={fields}
          onClose={() => setShowPreview(false)}
          title="Field Placement Preview"
        />
      )}
    </Layout>
  );
};

export default DocumentFieldEditor;
